/**
 * DTC Header Component
 * Header specifically for DTC pages - shows both global retailer selector AND DTC platform selector
 */

import React from 'react'
import { RefreshCw } from 'lucide-react'
import { RetailerSelector } from './RetailerSelector'

interface DtcHeaderProps {
  title: string
  subtitle?: string
  onRefresh?: () => void
  isLoading?: boolean
  // Platform selector props (passed from page)
  platforms?: Array<{ id: string; platform_name: string; platform_type: string }>
  selectedPlatformId?: string | null
  onPlatformChange?: (platformId: string | null) => void
}

export default function DtcHeader({ 
  title, 
  subtitle, 
  onRefresh, 
  isLoading,
  platforms,
  selectedPlatformId,
  onPlatformChange
}: DtcHeaderProps) {

  return (
    <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
      <div className="px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Title Section */}
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
            {subtitle && <p className="text-sm text-gray-600 mt-1">{subtitle}</p>}
          </div>

          {/* Controls Section */}
          <div className="flex items-center gap-4">
            {/* Global Retailer/Channel Selector (Retail vs DTC) */}
            <RetailerSelector showLabel={false} />
            
            {/* DTC Platform Selector (within DTC channel) */}
            {platforms && platforms.length > 0 && onPlatformChange && (
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700">Platform:</label>
                <select
                  value={selectedPlatformId || ''}
                  onChange={(e) => onPlatformChange(e.target.value || null)}
                  className="px-3 py-2 border border-purple-300 rounded-md text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-purple-50 min-w-[250px]"
                >
                  <option value="">All DTC Platforms</option>
                  {platforms.map((platform) => (
                    <option key={platform.id} value={platform.id}>
                      {platform.platform_name} ({platform.platform_type})
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Refresh Button */}
            {onRefresh && (
              <button
                onClick={onRefresh}
                disabled={isLoading}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:bg-gray-400 transition-colors"
              >
                <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                <span className="text-sm font-medium">Refresh</span>
              </button>
            )}
          </div>
        </div>

        {/* DTC Channel Badge */}
        <div className="mt-3">
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 border border-purple-300">
            🛒 Direct-to-Consumer Channel
          </span>
        </div>
      </div>
    </div>
  )
}

